import { TFunction } from 'i18next';
import { ACCOUNT_TYPE, DEFAULT_CONTENT_ROLES } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import z from 'zod';

import { INHERITANCE_STRATEGY } from './enum';

export const createUpadteDialogSchema = (
  originalName?: string,
  isWorkspace?: boolean,
  originalColor?: string,
  originalAssignees?: any[],
  t?: TFunction
) =>
  z
    .object({
      name: z.string().trim().min(1, t(T.resourceNameIsRequired)),
      color: z.string(),
      assignees: z
        .array(
          z.object({
            id: z.union([z.string(), z.number()]),
            name: z.string(),
            email: z.string().email().optional().nullable(),
            value: z.string(),
            type_id: z.union([z.string(), z.number()]).optional().nullable(),
            code: z.string().optional().nullable(),
            account_type: z
              .object({
                id: z.number(),
                name: z.string(),
                code: z.string(),
                type_id: z.number()
              })
              .optional()
              .nullable()
          })
        )
        .nullable()
        .optional()
        .default([])
        .transform((val) => val ?? [])
        .refine(
          (assignees) => {
            return !assignees.some(
              (u) => u?.account_type?.code === ACCOUNT_TYPE.REGULAR_USER
            );
          },
          {
            message: t(T.cannotAssignRegularUserAsController)
          }
        )
    })
    .superRefine((data, ctx) => {
      const hasUnauthorized = data.assignees?.some((u) => !u?.account_type);

      if (hasUnauthorized) {
        ctx.addIssue({
          code: 'custom',
          path: ['assignees'],
          message: t(T.unauthorizedUsersNotAllowedAsController)
        });
      }

      if (isWorkspace) {
        const nameChanged = data.name !== originalName;
        const colorChanged = data.color !== originalColor;
        const assigneesChanged =
          (originalAssignees?.length ?? 0) !== data.assignees.length ||
          !data.assignees.every((newAssignee: any) =>
            (originalAssignees ?? []).some(
              (oldAssignee: any) => oldAssignee.value === newAssignee.value
            )
          );
        if (!nameChanged && !colorChanged && !assigneesChanged) {
          ctx.addIssue({
            code: 'custom',
            path: ['name'],
            message: t(T.pleaseUpdateAtLeastOneField)
          });
        }
      } else {
        if (originalName && data.name === originalName) {
          ctx.addIssue({
            code: 'custom',
            path: ['name'],
            message: t(T.resourceNameUnchanged)
          });
        }
      }
    });

export const DataLibraryShareSchema = (isWorkspace: boolean, t?: TFunction) =>
  z
    .object({
      assignees: z
        .array(
          z.object({
            id: z.union([z.string(), z.number()]),
            name: z.string(),
            value: z.string(),
            type_id: z.union([z.string(), z.number()]).optional(),
            account_type: z
              .object({
                id: z.number(),
                name: z.string(),
                code: z.string(),
                type_id: z.number()
              })
              .optional()
              .nullable()
          })
        )
        .min(0, { message: t(T.selectAtLeastOneUserOrGroup) }),

      role: z
        .string({
          required_error: t(T.pleaseSelectRole)
        })
        .default(DEFAULT_CONTENT_ROLES.VIEWER),

      inheritanceStrategy: z
        .enum(
          [
            INHERITANCE_STRATEGY.DEFAULT,
            INHERITANCE_STRATEGY.FORCE,
            INHERITANCE_STRATEGY.FILES_ONLY
          ],
          {
            required_error: t(T.pleaseSelectAssignmentStrategy)
          }
        )
        .default(INHERITANCE_STRATEGY.DEFAULT),

      removedUsers: z.array(z.any()).default([]),
      updatedRoles: z.array(z.any()).default([])
    })
    .superRefine((data, ctx) => {
      if (isWorkspace && data.role === DEFAULT_CONTENT_ROLES.CONTROLLER) {
        const regularUsers = data.assignees.filter(
          (u) => u?.account_type?.code === ACCOUNT_TYPE.REGULAR_USER
        );

        if (regularUsers.length > 0) {
          ctx.addIssue({
            code: 'custom',
            message: t(T.cannotAssignRegularUserAsController),
            path: ['assignees'],
            fatal: true
          });
        }
      }
    });

export const dataLibraryWebLinkSchema = (t?: TFunction) =>
  z.object({
    collectionName: z
      .string({ required_error: t(T.collectionNameIsRequired) })
      .trim()
      .min(1, t(T.collectionNameIsRequired))
      .min(2, t(T.collectionNameTooShort)),
    links: z
      .array(
        z.object({
          url: z.string().url(t(T.invalidUrl)).or(z.literal(''))
        })
      )
      .superRefine((items, ctx) => {
        const nonEmptyUrls = items
          .map((item) => item.url.trim())
          .filter((url) => url !== '');

        if (nonEmptyUrls.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t(T.atLeastOneLinkRequired)
          });
          return;
        }

        const hasDuplicates = nonEmptyUrls.some(
          (url, index) => nonEmptyUrls.indexOf(url) !== index
        );

        if (hasDuplicates) {
          ctx.addIssue({
            code: 'custom',
            message: t(T.duplicateUrls)
          });
        }
      })
  });

export type DataLibraryWebLinkValues = z.infer<typeof dataLibraryWebLinkSchema>;
